import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/utils/api'
import { useCustodyParents, type CustodyParentsConfig } from '@/hooks/useCustodyParents'

export interface EventTypeOption {
  value: string
  label: string
}

export interface EventTypesResponse {
  family: EventTypeOption[]
  school: EventTypeOption[]
  district: EventTypeOption[]
  override: EventTypeOption[]
}

// Fallback definitions used when the API is unreachable. The override
// custody labels come from the family's custody_parents setting, matching
// the backend: at_parent1 is the primary parent, at_parent2 the other one.
const buildFallbackEventTypes = (parents: CustodyParentsConfig): EventTypesResponse => ({
  family: [
    { value: 'custody', label: "At Other Parent's House" },
    { value: 'school_activity', label: 'School Activity' },
    { value: 'sports', label: 'Sports & Recreation' },
    { value: 'work', label: 'Work' },
    { value: 'appointment', label: 'Appointment' },
    { value: 'celebration', label: 'Celebration' },
    { value: 'religious', label: 'Religious' },
    { value: 'travel', label: 'Travel' },
    { value: 'fun_activity', label: 'Fun Activity' },
    { value: 'caretaker', label: 'Caretaker/Relative' },
    { value: 'other', label: 'Other' },
  ],
  school: [
    { value: 'school_event', label: 'School Event' },
    { value: 'sports', label: 'Sports Event' },
    { value: 'club', label: 'Club Meeting' },
    { value: 'performance', label: 'Performance' },
    { value: 'other', label: 'Other' },
  ],
  district: [
    { value: 'holiday', label: 'Holiday' },
    { value: 'break', label: 'Break' },
    { value: 'early_release', label: 'Early Release' },
    { value: 'teacher_workday', label: 'Teacher Workday' },
    { value: 'testing', label: 'Testing Day' },
    { value: 'other', label: 'Other' },
  ],
  override: [
    { value: 'at_parent2', label: `At ${parents.other}'s House` },
    { value: 'at_parent1', label: `At ${parents.primary}'s House` },
    { value: 'work', label: 'Work' },
    { value: 'friend', label: "Friend's House" },
    { value: 'event', label: 'Event/Activity' },
    { value: 'school', label: 'School' },
    { value: 'other', label: 'Other' },
  ],
})

export function useEventTypes(): EventTypesResponse {
  const custodyParents = useCustodyParents()
  const { data } = useQuery<EventTypesResponse>({
    queryKey: ['event-types'],
    queryFn: async () => {
      const response = await apiClient.get('/api/calendar/event-types')
      return response as unknown as EventTypesResponse
    },
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
    retry: 1,
  })

  // The API's labels already carry the family's parent names; prefer them.
  return data ?? buildFallbackEventTypes(custodyParents)
}
